import { useState, useEffect } from "react";
import { sqlRead, sqlReadRaw, payloadOf } from "../utils/sqlClient";
import { ItemAssemblyPicker } from "../components/ItemAssemblyPicker";
import { btnPrimary } from "../styles/buttons";
import { useIsAdmin, AdminGate } from "../auth/AdminContext";

// Construction Standards — Room Boundary Intelligence's "Construction
// Standard rules engine" (see project memory): a room-template-pair ->
// default catalog entity mapping (e.g. "ISO 7 Cleanroom <-> Corridor =
// 1/2" Liner Panel System") that lets a high-confidence auto-detected wall
// (classifyConfidence() == 'auto_apply', src/utils/takeoffGeometry.js) on
// the Room Takeoff canvas get a real default type assigned automatically
// instead of sitting blank — closing the loop the Edge Halo/confidence-tier
// work (PRs #138-143) opened. See db/59_construction_standard_rules.sql for
// the full design rationale (order-independent pairing, retroactive-safe
// fill, why it points at Items/Assemblies rather than the legacy Condition
// Library path).
//
// Same load-read-only -> Edit -> Save/Cancel workflow as every other global
// reference table in this app (Quote Log Setup, Vendors, Cleanroom
// Classification) — nothing hits the database until "Save".

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "6px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12.5 };
const inputSm = { width: "100%", fontSize: 12, padding: "4px 7px", border: "1px solid var(--cs-border)", borderRadius: 5, boxSizing: "border-box", background: "var(--surface)", color: "var(--text)" };

const RELATIONSHIP_LABEL = { shared: "Shared wall", cavity: "Cavity / chase" };
// NULL bounds mean "unbounded" on that side.
const fmtGapRange = (min, max) => {
  if (min == null && max == null) return "any";
  if (min == null) return `≤ ${+max}′`;
  if (max == null) return `≥ ${+min}′`;
  return `${+min}′ – ${+max}′`;
};
// A blank input is "unbounded", not zero.
const numOrNull = v => (v === "" || v == null ? null : +v);

function RuleRow({ row, roomTemplates, items, assemblies, editing, isLocked, onEdit, onCancel, onSave, onDelete }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => { if (editing) setDraft(row); }, [editing, row]);

  const activeTemplates = roomTemplates.filter(t => t.IsActive || t.RoomTemplateID === row.RoomTemplateAID || t.RoomTemplateID === row.RoomTemplateBID);

  if (!editing) {
    return (
      <tr style={{ opacity: row.IsActive ? 1 : 0.5 }}>
        <td style={td}>{row.RoomTemplateAName} <span style={{ color: "var(--text3)" }}>&harr;</span> {row.RoomTemplateBName}</td>
        <td style={td}>{RELATIONSHIP_LABEL[row.RelationshipType] || <span style={{ color: "var(--text3)" }}>any</span>}</td>
        <td style={td}>{fmtGapRange(row.DistanceMinFt, row.DistanceMaxFt)}</td>
        <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{row.Priority ?? 100}</td>
        <td style={td}>
          <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.EntityCode || "—"}</span>{" "}
          <span style={{ color: "var(--text2)" }}>{row.EntityName}</span>
        </td>
        <td style={td}>{row.Notes || "—"}</td>
        <td style={{ ...td, textAlign: "right" }}>
          {!isLocked && (
            <>
              <button onClick={onEdit} style={{ fontSize: 11, marginRight: 6, cursor: "pointer" }}>Edit</button>
              {row.IsActive
                ? <button onClick={() => onDelete(row)} style={{ fontSize: 11, cursor: "pointer", color: "#b91c1c" }}>Retire</button>
                : <button onClick={() => onSave({ ...row, IsActive: 1 })} style={{ fontSize: 11, cursor: "pointer", color: "#166534" }}>Reactivate</button>}
            </>
          )}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={td}>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <select style={inputSm} value={draft.RoomTemplateAID ?? ""} onChange={e => setDraft({ ...draft, RoomTemplateAID: +e.target.value })}>
            <option value="">— pick —</option>
            {activeTemplates.map(t => <option key={t.RoomTemplateID} value={t.RoomTemplateID}>{t.Name}</option>)}
          </select>
          <span style={{ color: "var(--text3)" }}>&harr;</span>
          <select style={inputSm} value={draft.RoomTemplateBID ?? ""} onChange={e => setDraft({ ...draft, RoomTemplateBID: +e.target.value })}>
            <option value="">— pick —</option>
            {activeTemplates.map(t => <option key={t.RoomTemplateID} value={t.RoomTemplateID}>{t.Name}</option>)}
          </select>
        </div>
      </td>
      <td style={td}>
        <select style={inputSm} value={draft.RelationshipType ?? ""} onChange={e => setDraft({ ...draft, RelationshipType: e.target.value || null })}>
          <option value="shared">Shared wall</option>
          <option value="cavity">Cavity / chase</option>
          <option value="">Any</option>
        </select>
      </td>
      <td style={td}>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <input type="number" step="0.125" min="0" placeholder="min" style={{ ...inputSm, width: 64 }} value={draft.DistanceMinFt ?? ""}
            onChange={e => setDraft({ ...draft, DistanceMinFt: numOrNull(e.target.value) })} />
          <span style={{ color: "var(--text3)" }}>–</span>
          <input type="number" step="0.125" min="0" placeholder="max" style={{ ...inputSm, width: 64 }} value={draft.DistanceMaxFt ?? ""}
            onChange={e => setDraft({ ...draft, DistanceMaxFt: numOrNull(e.target.value) })} />
        </div>
      </td>
      <td style={td}>
        <input type="number" step="1" style={{ ...inputSm, width: 64, textAlign: "right" }} value={draft.Priority ?? 100}
          onChange={e => setDraft({ ...draft, Priority: e.target.value === "" ? 100 : +e.target.value })} />
      </td>
      <td style={td}>
        <ItemAssemblyPicker
          items={items} assemblies={assemblies}
          value={{ kind: draft.Kind ?? null, id: draft.Kind === "item" ? draft.ItemID : draft.AssemblyID }}
          onSelect={entity => setDraft({ ...draft, Kind: entity.kind, ItemID: entity.kind === "item" ? entity.id : null, AssemblyID: entity.kind === "assembly" ? entity.id : null })}
        />
      </td>
      <td style={td}><input style={inputSm} value={draft.Notes || ""} onChange={e => setDraft({ ...draft, Notes: e.target.value })} /></td>
      <td style={{ ...td, textAlign: "right" }}>
        <button onClick={() => onSave(draft)} style={{ fontSize: 11, marginRight: 6, cursor: "pointer", fontWeight: 700 }}>Save</button>
        <button onClick={onCancel} style={{ fontSize: 11, cursor: "pointer" }}>Cancel</button>
      </td>
    </tr>
  );
}

export default function ConstructionStandards() {
  // Admin-only (SWA-ESTIMATING-ADMIN) — a Setup page that had no page-level
  // gate of its own until 2026-09-11 (only the nav hid the link).
  const isAdmin = useIsAdmin();
  const [rules, setRules] = useState([]);
  const [roomTemplates, setRoomTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editingRuleId, setEditingRuleId] = useState(null);
  const [adding, setAdding] = useState(false);
  // A new rule defaults to the common case — a shared wall — so an admin has
  // to consciously choose "Any" before one entity gets applied to both a
  // common wall and a chase's two faces.
  const NEW_RULE = { RelationshipType: "shared", Priority: 100 };
  const [newRule, setNewRule] = useState(NEW_RULE);

  const load = () => {
    setLoading(true);
    setError("");
    return Promise.all([
      sqlRead("EXEC sp_GetConstructionStandardRules"),
      sqlRead("EXEC sp_GetRoomTemplates"),
      sqlReadRaw("EXEC sp_GetEstimateCostLibrary"),
    ])
      .then(([ruleRows, templateResult, libraryResult]) => {
        setRules(ruleRows || []);
        setRoomTemplates(templateResult?.Table1 || []);
        setAssemblies(libraryResult?.Table2 || []);
        setItems(libraryResult?.Table3 || []);
      })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise; see QuoteLogSetup.jsx's
  // identical comment for the exact blank-page bug this avoids.
  useEffect(() => { load(); }, []);

  async function saveRule(row) {
    try {
      const payload = payloadOf({
        rule_id: row.RuleID ?? null,
        room_template_a_id: row.RoomTemplateAID ?? null,
        room_template_b_id: row.RoomTemplateBID ?? null,
        kind: row.Kind || null,
        item_id: row.Kind === "item" ? row.ItemID : null,
        assembly_id: row.Kind === "assembly" ? row.AssemblyID : null,
        relationship_type: row.RelationshipType || null,
        distance_min_ft: numOrNull(row.DistanceMinFt),
        distance_max_ft: numOrNull(row.DistanceMaxFt),
        priority: row.Priority ?? 100,
        notes: row.Notes || null,
        display_order: row.DisplayOrder ?? 100,
        is_active: row.IsActive ?? 1,
      });
      const result = (await sqlRead(`EXEC sp_SaveConstructionStandardRule @payload = '${payload}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      setEditingRuleId(null);
      setAdding(false);
      setNewRule(NEW_RULE);
      load();
    } catch (e) { alert("Error saving rule: " + e.message); }
  }

  async function deleteRule(row) {
    if (!window.confirm(`Retire the "${row.RoomTemplateAName} ↔ ${row.RoomTemplateBName}" rule? Walls it already filled in keep their assignment — this only stops it from filling in new ones.`)) return;
    try {
      const payload = payloadOf({ rule_id: row.RuleID, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveConstructionStandardRule @payload = '${payload}'`);
      load();
    } catch (e) { alert("Error retiring rule: " + e.message); }
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  return (
    <div style={{ padding: "20px 24px", maxWidth: 1180 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Construction Standards</h2>
      <div style={{ fontSize: 12.5, color: "var(--text3)", marginBottom: 16, maxWidth: 640 }}>
        Room-type pair defaults for the Room Takeoff canvas's Edge Halo — a wall the engine is already
        confident about (the calm/grey "auto_apply" tier) gets filled in automatically from the matching
        rule below instead of sitting blank. Lower-confidence walls always stay unassigned for a human look.
        A pair can carry several rules: narrow each by <b>relationship</b> (shared wall vs. cavity/chase) and
        <b> gap range</b> (ft, inclusive; blank = unbounded); when more than one matches, the highest
        <b> priority</b> wins — two different defaults tied at the top are ambiguous and the wall is left blank.
      </div>

      {error && <div style={{ color: "#b91c1c", fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      {loading ? (
        <div style={{ fontSize: 12.5, color: "var(--text3)" }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            {!adding && <button onClick={() => setAdding(true)} style={{ ...btnPrimary, fontSize: 11.5, padding: "4px 10px" }}>+ Add rule</button>}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Room Types</th>
                <th style={th}>Relationship</th>
                <th style={th}>Gap range</th>
                <th style={{ ...th, textAlign: "right" }}>Priority</th>
                <th style={th}>Default</th>
                <th style={th}>Notes</th>
                <th style={{ ...th, textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(row => (
                <RuleRow
                  key={row.RuleID}
                  row={row} roomTemplates={roomTemplates} items={items} assemblies={assemblies}
                  editing={editingRuleId === row.RuleID}
                  onEdit={() => setEditingRuleId(row.RuleID)}
                  onCancel={() => setEditingRuleId(null)}
                  onSave={saveRule}
                  onDelete={deleteRule}
                />
              ))}
              {adding && (
                <RuleRow
                  row={newRule} roomTemplates={roomTemplates} items={items} assemblies={assemblies}
                  editing={true}
                  onCancel={() => setAdding(false)}
                  onSave={saveRule}
                />
              )}
              {rules.length === 0 && !adding && (
                <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--text3)" }}>No rules yet — add one above.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
